import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { Button } from './ui/Button';
import { formatWineType, type Wine } from '@wineryxplorer/shared';

export interface WineSelectorProps {
  venueId: string;
  selectedWineIds: string[];
  onSelectionChange: (wineIds: string[]) => void;
  disabled?: boolean;
  maxSelection?: number;
}

export const WineSelector = ({
  venueId,
  selectedWineIds,
  onSelectionChange,
  disabled = false,
  maxSelection,
}: WineSelectorProps) => {
  const [showSelector, setShowSelector] = useState(false);

  // Fetch wines for this venue
  const { data: winesData, isLoading } = trpc.wine.listByVenue.useQuery(
    { venueId, limit: 100 },
    { enabled: showSelector || selectedWineIds.length > 0 }
  );

  // Filter selected wines from the full list
  const wines = (winesData?.items || []) as Wine[];
  const selectedWines = wines.filter((wine) => selectedWineIds.includes(wine._id));

  const handleToggleWine = (wineId: string) => {
    if (selectedWineIds.includes(wineId)) {
      onSelectionChange(selectedWineIds.filter(id => id !== wineId));
    } else {
      if (maxSelection && selectedWineIds.length >= maxSelection) {
        return;
      }
      onSelectionChange([...selectedWineIds, wineId]);
    }
  };

  const handleRemoveWine = (wineId: string) => {
    onSelectionChange(selectedWineIds.filter(id => id !== wineId));
  };

  return (
    <div className="space-y-3">
      {/* Selected Wines Display */}
      {selectedWineIds.length > 0 && selectedWines.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Selected Wines ({selectedWineIds.length}
            {maxSelection && ` / ${maxSelection}`})
          </p>
          <div className="space-y-2">
            {selectedWines.map((wine) => (
              <div
                key={wine._id}
                className="flex items-center justify-between p-3 bg-primary-50 border border-primary-200 rounded-md"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{wine.name}</p>
                  <p className="text-xs text-gray-600">
                    {formatWineType(wine.wineType)}
                    {wine.vintage && ` • ${wine.vintage}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveWine(wine._id)}
                  disabled={disabled}
                  className="ml-3 text-red-600 hover:text-red-800 disabled:opacity-50"
                  aria-label="Remove wine"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Wines Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowSelector(!showSelector)}
        disabled={disabled || (maxSelection ? selectedWineIds.length >= maxSelection : false)}
      >
        {showSelector ? 'Hide Wine List' : 'Add Wines'}
      </Button>

      {/* Wine Selection List */}
      {showSelector && (
        <div className="border border-gray-300 rounded-md p-4 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="text-sm text-gray-600 mt-2">Loading wines...</p>
            </div>
          ) : winesData && winesData.items.length > 0 ? (
            <div className="space-y-2">
              {wines.map((wine) => {
                const isSelected = selectedWineIds.includes(wine._id);
                const isDisabled = disabled || (!isSelected && Boolean(maxSelection && selectedWineIds.length >= maxSelection));
                
                return (
                  <button
                    key={wine._id}
                    type="button"
                    onClick={() => handleToggleWine(wine._id)}
                    disabled={isDisabled}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      isSelected
                        ? 'bg-primary-50 border-primary-300'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-50'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        disabled={isDisabled}
                        className="h-4 w-4 text-primary-700 focus:ring-info-500 border-gray-300 rounded mr-3"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{wine.name}</p>
                        <p className="text-xs text-gray-600">
                          {formatWineType(wine.wineType)}
                          {wine.vintage && ` • ${wine.vintage}`}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-600">No wines available for this venue</p>
              <p className="text-xs text-gray-500 mt-1">Add wines to the venue first</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
